import { useQuery } from '@tanstack/react-query';
import { ArrowRightLeft } from 'lucide-react';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { DamagePolygonType } from '@/common/damage-polygon';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { useDamageMutations } from '@/hooks/use-damage-mutations';
import { getLanguage } from '@/i18n';
import { numberLocaleFor } from '@/i18n/format';
import { useQueryListInspectionsOptions } from '@/queries/inspections/list-inspections';

type MoveDamageDialogProps = {
	isOpen: boolean;
	onClose: () => void;
	assetId: string;
	currentInspectionId: string;
	damageId: string;
	damageTitle: string;
	onMoveSuccess: () => void;
	polygons?: DamagePolygonType[];
};

export function MoveDamageDialog({
	isOpen,
	onClose,
	assetId,
	currentInspectionId,
	damageId,
	damageTitle,
	onMoveSuccess,
	polygons = [],
}: MoveDamageDialogProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [targetInspectionId, setTargetInspectionId] = useState<string>('');

	// Use moveMutation logic
	const { moveMutation, isSubmitting } = useDamageMutations({
		assetId,
		inspectionId: currentInspectionId,
		damageId,
		onMoveSuccess: (targetId) => {
			onClose();
			onMoveSuccess();
			navigate(`/assets/${assetId}/inspections/${targetId}?tab=damages`);
		},
	});

	// Fetch available inspections
	const { data: inspectionsResponse, isLoading: isLoadingInspections } = useQuery(
		useQueryListInspectionsOptions(assetId, { limit: 1000, offset: 0 }, isOpen)
	);

	const availableInspections =
		inspectionsResponse?.inspections.filter((insp) => insp.id !== currentInspectionId) || [];

	const handleMove = () => {
		if (!targetInspectionId) return;
		// Trigger the dedicated move mutation
		moveMutation.mutate({ targetInspectionId, polygons });
	};

	const polygonsCount = polygons.length;

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="sm:max-w-106.25">
				<DialogHeader>
					<DialogTitle>{t('damages.moveDialog.title')}</DialogTitle>
					<DialogDescription>
						<Trans
							i18nKey="damages.moveDialog.description"
							values={{ title: damageTitle }}
							components={{ strong: <strong /> }}
						/>
						{polygonsCount > 0 && (
							<span className="block mt-1 text-amber-600">
								{t('damages.moveDialog.polygonsNote', { count: polygonsCount })}
							</span>
						)}
					</DialogDescription>
				</DialogHeader>

				<div className="grid gap-4 py-4">
					<div className="flex flex-col gap-2">
						<label className="text-sm font-medium text-muted-foreground">
							{t('damages.moveDialog.selectTargetInspection')}
						</label>
						{isLoadingInspections ? (
							<div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
								<Spinner className="h-4 w-4" /> {t('damages.moveDialog.loadingInspections')}
							</div>
						) : availableInspections.length === 0 ? (
							<div className="text-sm text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
								{t('damages.moveDialog.noOtherInspections')}
							</div>
						) : (
							<Select value={targetInspectionId} onValueChange={setTargetInspectionId}>
								<SelectTrigger className="w-full">
									<SelectValue placeholder={t('damages.moveDialog.selectInspectionPlaceholder')} />
								</SelectTrigger>
								<SelectContent className="max-h-75">
									{availableInspections.map((insp) => (
										<SelectItem key={insp.id} value={insp.id}>
											{/* Adjust display logic based on InspectionType fields */}
											{insp.title
												? insp.title
												: t('damages.moveDialog.inspectionFallback', {
														date: insp.date
															? new Date(insp.date).toLocaleDateString(numberLocaleFor(getLanguage()))
															: '',
													})}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						)}
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={isSubmitting}>
						{t('common.actions.cancel')}
					</Button>
					<Button
						onClick={handleMove}
						disabled={!targetInspectionId || isSubmitting}
					>
						{isSubmitting && <Spinner className="mr-2 h-4 w-4" />}
						<ArrowRightLeft className="mr-2 h-4 w-4" />
						{t('damages.moveDialog.title')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
